import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import * as validators from '../../util/validators';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  FieldCheckbox,
  FieldPhoneNumberInput,
  FieldSelect,
} from '../../components';

import css from './ConfirmSignupForm.module.css';

const KEY_CODE_ENTER = 13;

export const ConfirmSignupFormComponent = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        values,
        intl,
        onOpenTermsOfService,
        authInfo,
        idp,
        role,
      } = formRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // firstName
      const firstNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameLabel',
      });
      const firstNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNamePlaceholder',
      });
      const firstNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameRequired',
      });
      const firstNameRequired = validators.required(firstNameRequiredMessage);

      // lastName
      const lastNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameLabel',
      });
      const lastNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNamePlaceholder',
      });
      const lastNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameRequired',
      });
      const lastNameRequired = validators.required(lastNameRequiredMessage);

      // phone number
      const phoneLabel = intl.formatMessage({ id: 'ConfirmSignupForm.phoneLabel' });
      const phonePlaceholder = intl.formatMessage({ id: 'ConfirmSignupForm.phonePlaceholder' });

      // organization name
      const organizationNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.organizationNameLabel',
      });
      const organizationNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.organizationNamePlaceholder',
      });
      const organizationNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.organizationNameRequired',
      });
      const organizationNameRequired = validators.required(organizationNameRequiredMessage);

      // role selector
      const roleLabel = intl.formatMessage({ id: 'ConfirmSignupForm.roleLabel' });
      const roleRequiredMessage = intl.formatMessage({ id: 'ConfirmSignupForm.roleRequired' });
      const roleRequired = validators.required(roleRequiredMessage);

      // Determine selected role (from prop or form value)
      const selectedRole = role || values.role;
      const showRoleSelector = !role;
      const isCreator = selectedRole === 'creator';
      const isOrganization = selectedRole === 'organization';
      const showPhoneNumber = isCreator || isOrganization;
      const showOrganizationName = isOrganization;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      // FieldCheckbox is a multi-checkbox: its value is the array of ticked values
      const ageConfirmed = Array.isArray(values.ageConfirmed) && values.ageConfirmed.length > 0;
      const submitDisabled = invalid || submitInProgress || !ageConfirmed;

      const handleTermsKeyUp = e => {
        // Allow click action with keyboard like with normal links
        if (e.keyCode === KEY_CODE_ENTER) {
          onOpenTermsOfService();
        }
      };
      const termsLink = (
        <span
          className={css.termsLink}
          onClick={onOpenTermsOfService}
          role="button"
          tabIndex="0"
          onKeyUp={handleTermsKeyUp}
        >
          <FormattedMessage id="ConfirmSignupForm.termsAndConditionsLinkText" />
        </span>
      );

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName } = authInfo;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            {showRoleSelector && (
              <FieldSelect
                className={css.field}
                id={formId ? `${formId}.role` : 'role'}
                name="role"
                label={roleLabel}
                validate={roleRequired}
              >
                <option value="">
                  {intl.formatMessage({ id: 'ConfirmSignupForm.rolePlaceholder' })}
                </option>
                <option value="audience">
                  {intl.formatMessage({ id: 'ConfirmSignupForm.roleAudience' })}
                </option>
                <option value="creator">
                  {intl.formatMessage({ id: 'ConfirmSignupForm.roleCreator' })}
                </option>
                <option value="organization">
                  {intl.formatMessage({ id: 'ConfirmSignupForm.roleOrganization' })}
                </option>
              </FieldSelect>
            )}

            <FieldTextInput
              className={css.field}
              type="text"
              id={formId ? `${formId}.firstName` : 'firstName'}
              name="firstName"
              autoComplete="given-name"
              label={firstNameLabel}
              placeholder={firstNamePlaceholder}
              initialValue={firstName}
              validate={firstNameRequired}
            />

            <FieldTextInput
              className={css.field}
              type="text"
              id={formId ? `${formId}.lastName` : 'lastName'}
              name="lastName"
              autoComplete="family-name"
              label={lastNameLabel}
              placeholder={lastNamePlaceholder}
              initialValue={lastName}
              validate={lastNameRequired}
            />

            {showOrganizationName && (
              <FieldTextInput
                className={css.field}
                type="text"
                id={formId ? `${formId}.organizationName` : 'organizationName'}
                name="organizationName"
                autoComplete="organization"
                label={organizationNameLabel}
                placeholder={organizationNamePlaceholder}
                validate={organizationNameRequired}
              />
            )}

            {/* Age: a required "I'm 18 or older" checkbox, no date of birth (Privacy B.1) */}
            <FieldCheckbox
              className={css.field}
              id={formId ? `${formId}.ageConfirmed` : 'ageConfirmed'}
              name="ageConfirmed"
              value="confirmed"
              label={<FormattedMessage id="SignupForm.ageConfirm" />}
            />

            <FieldTextInput
              className={css.field}
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              initialValue={email}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />

            {showPhoneNumber && (
              <FieldPhoneNumberInput
                className={css.field}
                name="phoneNumber"
                id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                label={phoneLabel}
                placeholder={phonePlaceholder}
              />
            )}
          </div>

          <div className={css.bottomWrapper}>
            <p className={css.bottomWrapperText}>
              <span className={css.termsText}>
                <FormattedMessage
                  id="ConfirmSignupForm.termsAndConditionsAcceptText"
                  values={{ termsLink }}
                />
              </span>
            </p>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

ConfirmSignupFormComponent.defaultProps = {
  inProgress: false,
  role: null,
};

const { bool, func, string, oneOf } = PropTypes;

ConfirmSignupFormComponent.propTypes = {
  inProgress: bool,
  role: oneOf(['audience', 'creator', 'organization', null]),

  onOpenTermsOfService: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ConfirmSignupForm = compose(injectIntl)(ConfirmSignupFormComponent);
ConfirmSignupForm.displayName = 'ConfirmSignupForm';

export default ConfirmSignupForm;
